import { formatPattern } from 'react-router';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import {
  AssignmentIcon,
  CurriculumIcon,
  EmailIcon,
  HomeIcon,
  MembersIcon,
  ReadingIcon,
  SettingsIcon,
  StarIcon,
  TrendingIcon,
  UserIcon,
} from '@coursera/cds-icons';

import type { SideNavIconComponent } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';
import {
  ANALYTICS_DASHBOARDS_PATH,
  ANALYTICS_PATH,
  ANALYTICS_REPORTS_PATH,
  ASSIGNED_CONTENT_CATALOG_PATH,
  CONTRACTS_PATH,
  CURATED_CATALOG_PATH,
  CUSTOM_COURSES_PATH,
  GROUPS_PATH,
  HOME_PATH,
  LEARNERS_PATH,
  MESSAGES_PATH,
  MESSAGING_PATH,
  ORG_SKILLSETS_PATH,
  ORG_SKILLS_PATH,
  PROGRAMS_PATH,
  SETTINGS_PATH,
  USERS_PATH,
} from 'bundles/enterprise-admin-navigation/utils/pathConstants';
import {
  ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN as SUB,
  ENTERPRISE_ADMIN_PERMISSION_VERB as VERB,
} from 'bundles/enterprise-admin-roles/types';
import type { RequiredPermission } from 'bundles/enterprise-admin-roles/types';
import { showOrgLevelSkillsForOrg } from 'bundles/enterprise-admin-skills/utils/common';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

import _t from 'i18n!nls/enterprise-admin-navigation';

/**
 * Runtime context resolved once per render by `useSideNavItems`.
 * Passed to every `shouldShow` predicate and override so items can make
 * data-driven decisions (e.g. "show this only if user has permission X").
 *
 * Built from existing hooks (router, permissions, flags) so consumers stay
 * simple — no need to thread dependencies individually to every item.
 *
 * Properties:
 * - router: Router instance for navigation checks and active state.
 * - thirdPartyOrganization: Current organization data.
 * - enableSkillsPlanningForOrg: Feature flag for skills planning.
 * - enableNewLearnersPage: Feature flag for new learners page.
 * - enableMessagingV2: Product feature flag that points the Messages nav at the new /admin/messaging route.
 * - enableLearnerGroups: Product feature flag that expands the Learners item into "All learners" + "Groups" sub-items,
 *   and also gates the "Catalog" item ("Curated catalog" + "Assigned content" sub-items) — Curated Catalog is Phase 2
 *   of the same decoupling-content-from-programs initiative that Groups (Phase 1) shipped, so it's only relevant once
 *   an org is on Groups. Also hides the "Custom Courses", "Programs", and "SkillSets"/"Skills" items, which are
 *   superseded by the Groups-based navigation.
 * - hasPermission: Function to check if user has specific permission.
 * - isOrganizationDomainAdmin: Whether user is organization domain admin (permission-hydration based; used by
 *   Settings (with `!isStaffAdmin`) and the Curated-catalog org-admin gate — preferred over adminRoleType for
 *   org admins since it survives multi-role admins whose primary role template isn't "Organization Admin". Note
 *   it's also true for staff admins, who hold org-level permissions — so it does NOT distinguish a genuine org
 *   admin from staff).
 * - isOrgAdmin: Whether the admin's role type is Organization Admin (adminRoleType-based). Unlike
 *   `isOrganizationDomainAdmin`, this is false for staff admins, so it gates the org-admin-only "Assigned content".
 * - isGroupAdmin: Whether the admin's role type is Program/Group Admin (adminRoleType-based).
 * - isStaffAdmin: Whether the admin's role type is Staff Admin / PII-restricted (adminRoleType-based). Used to
 *   hide Settings from staff, who otherwise pass `isOrganizationDomainAdmin`.
 * - isMentorAdmin: Whether the admin's role type is Mentor (adminRoleType-based). Used to grant mentor admins
 *   access to Learners > Groups and Catalog > Curated catalog, which their permissions alone wouldn't unlock.
 * - canUseCourseBuilder: Whether user can use course builder.
 * - isSuperUserOrEnterpriseOutsourcing: Enterprise-level permissions check.
 * - isFreemiumOrgAndCanOnlyManagePrograms: Freemium org limitation flag.
 * - locationSearchString: Query params carried over during navigation (ref=ADMIN_HOME_V2_REF stripped).
 * - isUnifiedSsoEnabled: Product feature flag for unified SSO (gates SAML-only settings nav items).
 * - isActiveHref: Predicate for whether a resolved href is active (exact match in settings, prefix match elsewhere).
 */
export type AdminSideNavRuntimeContext = {
  router: InjectedRouter;
  thirdPartyOrganization: AdminDashboardOrg;
  enableSkillsPlanningForOrg: boolean;
  enableNewLearnersPage: boolean;
  enableMessagingV2: boolean;
  enableLearnerGroups: boolean;
  hasPermission: (perm: RequiredPermission) => boolean;
  isOrganizationDomainAdmin: boolean;
  isOrgAdmin: boolean;
  isGroupAdmin: boolean;
  isStaffAdmin: boolean;
  isMentorAdmin: boolean;
  canUseCourseBuilder: boolean;
  isSuperUserOrEnterpriseOutsourcing: boolean;
  isFreemiumOrgAndCanOnlyManagePrograms: boolean;
  locationSearchString: string;
  isUnifiedSsoEnabled: boolean;
  isActiveHref: (href: string) => boolean;
};

/**
 * AdminSideNavSubItemConfig defines the shape of each sub-nav item.
 *
 * Sub-nav rows are produced from a parent `children` entry and mirror `SideNavSubItem`
 * structure but without icon or nested children properties.
 *
 * Properties:
 * - id: Unique string identifier for the sub-item.
 * - getLabel: Function that receives runtime context and returns the display string.
 * - href: Route template (':thirdPartySlug' substituted at runtime).
 * - requiredPermissions: (Optional) Permission(s) needed to see/select this item.
 * - shouldShow: (Optional) Predicate that decides visibility based on runtime context.
 * - isActiveOverride: (Optional) Custom active-state predicate. Defaults to router.isActive(href).
 */
export type AdminSideNavSubItemConfig = {
  id: string;
  getLabel: (runtimeContext: AdminSideNavRuntimeContext) => string;
  href: string;
  requiredPermissions?: RequiredPermission;
  shouldShow?: (runtimeContext: AdminSideNavRuntimeContext) => boolean;
  isActiveOverride?: (runtimeContext: AdminSideNavRuntimeContext, resolvedHref: string) => boolean;
};

/**
 * AdminSideNavItemConfig defines the shape of each side-nav item.
 *
 * Pure data structure — no React, no closures, no action callbacks. Walked at runtime by
 * `useSideNavItems`. Adding/removing/reordering/nesting an item is a one-array edit
 * on the `sideNavConfigDefs.top` or `sideNavConfigDefs.bottom` arrays.
 *
 * Each item's `shouldShow` predicate receives the runtime context and decides whether
 * to render the item. Returning false drops the item from the list.
 *
 * Either `href` or `children` must be present — leaf items link directly; expandable
 * parents can omit `href` when they only expose sub-nav links.
 *
 * Properties:
 * - id: Unique string identifier for the item.
 * - getLabel: Function that receives runtime context and returns the display string.
 * - icon: Icon component displayed alongside the label.
 * - href: (Optional) Static route template (':thirdPartySlug' substituted at runtime). Required for leaf items.
 * - hrefOverride: (Optional) Dynamic href that wins over `href` when set (e.g., Skills vs SkillSets).
 * - requiredPermissions: (Optional) Permission(s) needed to see/select this item.
 * - shouldShow: (Optional) Predicate that decides visibility based on runtime context.
 * - isActiveOverride: (Optional) Custom active-state predicate (e.g., Programs vs /contracts mutex). Defaults to router.isActive(href).
 * - children: (Optional) Sub-nav links rendered via `SideNavExpandableItem`. Required when `href` is omitted.
 * - showNewBadge: (Optional) Predicate deciding whether a "New" tag renders next to the label.
 */
type AdminSideNavItemConfigBase = {
  id: string;
  getLabel: (runtimeContext: AdminSideNavRuntimeContext) => string;
  icon: SideNavIconComponent;
  hrefOverride?: (runtimeContext: AdminSideNavRuntimeContext) => string;
  requiredPermissions?: RequiredPermission;
  shouldShow?: (runtimeContext: AdminSideNavRuntimeContext) => boolean;
  isActiveOverride?: (runtimeContext: AdminSideNavRuntimeContext, resolvedHref: string) => boolean;
  showNewBadge?: (runtimeContext: AdminSideNavRuntimeContext) => boolean;
};

export type AdminSideNavItemConfig =
  | (AdminSideNavItemConfigBase & { href: string; children?: never })
  | (AdminSideNavItemConfigBase & { href?: never; children: AdminSideNavSubItemConfig[] });

/** Top and bottom side-nav item lists rendered in their respective `SideNavBar` slots. */
export type AdminSideNavConfigDefs = {
  top: AdminSideNavItemConfig[];
  bottom: AdminSideNavItemConfig[];
};

/**
 * The Messages "New" badge is a launch affordance for Messaging V2 (launched
 * 2026-07-09). It stops showing on 2026-08-10 — one month later. Compared
 * against `Date.now()` at render so it self-expires with no follow-up cleanup.
 */
export const MESSAGING_V2_NEW_BADGE_END_MS = Date.UTC(2026, 7, 10); // 2026-08-10T00:00:00Z

/**
 * Keeps a section's nav item active on its detail / sub-routes, not just its exact landing page.
 * react-router v3 `isActive` only matches the exact active route branch, and detail pages like
 * `curated-catalog/detail` are registered as sibling routes (not nested under the section route),
 * so `isActive(sectionHref)` is false there. Fall back to a pathname-prefix check on the section
 * base so e.g. `/catalog/curated-catalog/detail` still marks "Catalog › Curated catalog" active.
 */
const isActiveWithinSection = (runtimeContext: AdminSideNavRuntimeContext, href: string): boolean =>
  runtimeContext.isActiveHref(href) || runtimeContext.router.location.pathname.startsWith(`${href}/`);

/**
 * The single source of truth for the new admin side-nav items.
 *
 * To add an item: drop a new entry into the array (with a `shouldShow` if it should
 * be conditionally visible). To remove one: delete the entry. To nest: add
 * `children`.
 *
 * Exported as a constant to prevent array recreation on every hook invocation.
 * Labels are resolved at render time via the config predicates and resolvers.
 *
 * Note: _t() calls within getLabel functions execute at hook runtime (both server/client),
 * ensuring consistent SSR behavior. The eslint warning is overly cautious for this pattern.
 */
export const sideNavConfigDefs: AdminSideNavConfigDefs = {
  top: [
    {
      id: 'ADMIN_HOME',
      getLabel: () => _t('Home'),
      icon: HomeIcon,
      href: HOME_PATH,
    },
    {
      // Behind enableLearnerGroups, the flat Learners link becomes an expandable parent
      // with "All learners" and "Groups" sub-items. Gating moved onto each child (rather
      // than the parent) so mentor admins can see Groups without All learners, which
      // exposes learner PII they shouldn't have access to.
      id: 'LEARNERS_GROUPED',
      getLabel: () => _t('Learners'),
      icon: UserIcon,
      shouldShow: (runtimeContext) => runtimeContext.enableNewLearnersPage && runtimeContext.enableLearnerGroups,
      children: [
        {
          id: 'LEARNERS_ALL',
          getLabel: () => _t('All learners'),
          href: LEARNERS_PATH,
          requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.LEARNERS, verb: VERB.MANAGE },
        },
        {
          id: 'LEARNERS_GROUPS',
          getLabel: () => _t('Groups'),
          href: GROUPS_PATH,
          shouldShow: (runtimeContext) =>
            runtimeContext.isMentorAdmin ||
            runtimeContext.hasPermission({ domain: 'ANY_PROGRAM', subdomain: SUB.LEARNERS, verb: VERB.MANAGE }),
        },
      ],
    },
    {
      // Curated Catalog — decoupling content from learning programs, phase 2. Org-scoped
      // catalog of collections/learning paths/custom courses, replacing per-program curation.
      // The parent only gates on the feature flag; per-role visibility lives on each sub-item
      // below (Curated catalog: org-domain/group/staff/mentor; Assigned content: role-based org/group admin
      // only). When an admin can see neither sub-item, `resolveItem` drops the empty parent automatically.
      // No row/action-level gating here — that's a per-row concern for the table itself.
      id: 'CATALOG',
      getLabel: () => _t('Catalog'),
      icon: ReadingIcon,
      shouldShow: (runtimeContext) => runtimeContext.enableLearnerGroups,
      children: [
        {
          // Visible to org, group, staff, and mentor admins.
          id: 'CURATED_CATALOG',
          getLabel: () => _t('Curated catalog'),
          href: CURATED_CATALOG_PATH,
          // Stay active on the item detail page (`curated-catalog/detail`, a sibling route).
          isActiveOverride: isActiveWithinSection,
          shouldShow: (runtimeContext) =>
            runtimeContext.isOrganizationDomainAdmin ||
            runtimeContext.isGroupAdmin ||
            runtimeContext.isStaffAdmin ||
            runtimeContext.isMentorAdmin,
        },
        {
          // Visible to role-based org and group admins only — not staff, who pass the
          // permission-based `isOrganizationDomainAdmin` gate but shouldn't see Assigned content, and
          // not mentor admins, who are permanently excluded by the page's own
          // `useCatalogAssignedContentAccess` gate (org/group admin only, by design).
          id: 'ASSIGNED_CONTENT_CATALOG',
          getLabel: () => _t('Assigned content'),
          href: ASSIGNED_CONTENT_CATALOG_PATH,
          // Stay active on assigned-content sub-routes (e.g. the insights pages).
          isActiveOverride: isActiveWithinSection,
          shouldShow: (runtimeContext) => runtimeContext.isOrgAdmin || runtimeContext.isGroupAdmin,
        },
      ],
    },
    {
      id: 'CUSTOM_COURSES',
      getLabel: () => _t('Custom Courses'),
      icon: CurriculumIcon,
      href: CUSTOM_COURSES_PATH,
      shouldShow: (runtimeContext) => runtimeContext.canUseCourseBuilder && !runtimeContext.enableLearnerGroups,
      requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.CUSTOM_COURSES, verb: VERB.VIEW },
    },
    {
      id: 'PROGRAMS',
      getLabel: () => _t('Programs'),
      icon: MembersIcon,
      href: PROGRAMS_PATH,
      shouldShow: (runtimeContext) => !runtimeContext.enableLearnerGroups,
      isActiveOverride: (runtimeContext, href) =>
        runtimeContext.router.isActive(href) && !runtimeContext.router.location.pathname.includes('/contracts'),
    },
    {
      id: 'ORG_TSPS',
      // Skills replaces SkillSets for orgs flagged via showOrgLevelSkillsForOrg.
      getLabel: (runtimeContext) =>
        showOrgLevelSkillsForOrg(runtimeContext.thirdPartyOrganization.id) ? _t('Skills') : _t('SkillSets'),
      icon: StarIcon,
      href: ORG_SKILLSETS_PATH,
      hrefOverride: (runtimeContext) =>
        showOrgLevelSkillsForOrg(runtimeContext.thirdPartyOrganization.id) ? ORG_SKILLS_PATH : ORG_SKILLSETS_PATH,
      shouldShow: (runtimeContext) => runtimeContext.enableSkillsPlanningForOrg && !runtimeContext.enableLearnerGroups,
      requiredPermissions: { domain: 'ORGANIZATION', subdomain: SUB.SKILLSETS, verb: VERB.EDIT },
    },
    {
      id: 'LEARNERS',
      getLabel: () => _t('Learners'),
      icon: UserIcon,
      href: LEARNERS_PATH,
      shouldShow: (runtimeContext) => runtimeContext.enableNewLearnersPage && !runtimeContext.enableLearnerGroups,
      requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.LEARNERS, verb: VERB.MANAGE },
    },
    {
      id: 'USERS',
      getLabel: () => _t('Users'),
      icon: MembersIcon,
      href: USERS_PATH,
      shouldShow: (runtimeContext) => !runtimeContext.enableNewLearnersPage,
      requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.LEARNERS, verb: VERB.MANAGE },
    },
    {
      id: 'MESSAGES',
      getLabel: () => _t('Messages'),
      icon: EmailIcon,
      // href is never read at runtime — hrefOverride is always set and wins in resolveItem.
      // It's kept to document the default destination when Messaging V2 is disabled.
      href: MESSAGES_PATH,
      // Messaging V2 moves the destination to /admin/messaging; fall back to the legacy
      // automated-messages list until the org is enabled for the product feature.
      hrefOverride: (runtimeContext) => (runtimeContext.enableMessagingV2 ? MESSAGING_PATH : MESSAGES_PATH),
      requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.COMMUNICATIONS, verb: VERB.VIEW },
      // Flag the new Messaging V2 experience for a month after launch (see MESSAGING_V2_NEW_BADGE_END_MS).
      showNewBadge: (runtimeContext) => runtimeContext.enableMessagingV2 && Date.now() < MESSAGING_V2_NEW_BADGE_END_MS,
    },
    {
      // Expandable parent surfacing the Analytics "Dashboards" and "Reports" sub-pages.
      id: 'ANALYTICS',
      getLabel: () => _t('Analytics'),
      icon: TrendingIcon,
      shouldShow: (runtimeContext) => !runtimeContext.isFreemiumOrgAndCanOnlyManagePrograms,
      requiredPermissions: { domain: 'ANY_PROGRAM', subdomain: SUB.ANALYTICS, verb: VERB.VIEW },
      children: [
        {
          id: 'ANALYTICS_DASHBOARDS',
          getLabel: () => _t('Dashboards'),
          href: ANALYTICS_DASHBOARDS_PATH,
          isActiveOverride: (runtimeContext) => {
            const { router } = runtimeContext;
            const analyticsBase = formatPattern(ANALYTICS_PATH, router.params);
            const reportsBase = formatPattern(ANALYTICS_REPORTS_PATH, router.params);
            return router.isActive(analyticsBase) && !router.isActive(reportsBase);
          },
        },
        {
          id: 'ANALYTICS_REPORTS',
          getLabel: () => _t('Reports'),
          href: ANALYTICS_REPORTS_PATH,
        },
      ],
    },
    {
      id: 'CONTRACTSV2',
      getLabel: () => _t('Contracts'),
      icon: AssignmentIcon,
      href: CONTRACTS_PATH,
      shouldShow: (runtimeContext) => runtimeContext.isSuperUserOrEnterpriseOutsourcing,
    },
  ],
  bottom: [
    {
      id: 'SETTINGS',
      getLabel: () => _t('Settings'),
      icon: SettingsIcon,
      href: SETTINGS_PATH,
      shouldShow: (runtimeContext) => runtimeContext.isOrganizationDomainAdmin && !runtimeContext.isStaffAdmin,
    },
  ],
};
